import { Injectable } from '@angular/core';

import { OfferSource } from '@cds';
import { PopularBetsOptionTeamInfo } from '@cds/betting-offer/domain-specific/popular-bets';
import { EventOption, EventParticipant, EventParticipantImage } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig, ImagesConfig, PopularBetsConfig, SitecorePopularBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { ImageRootPath } from '@frontend/sports/participant-image/feature';
import { FallbackImage } from '@frontend/sports/participant-image/feature/provider';
import { PopularBetsPodTypes } from '@frontend/sports/types/components/popular-bets';
import { PopularBetsFallbackLogoModel } from '@frontend/sports/types/models/popular-bets';
import { UtilsService } from '@frontend/vanilla/core';

import { PopularBetsPodEventLogoDetails } from '../popular-bets.models';
import { PopularBetsPodEventParticipantService } from './popular-bets-pod-event-participant.service';

@Injectable({
    providedIn: 'root',
})
export class PopularBetsPodEventLogoService {
    private logosDict: {
        [sportId: number]: {
            sportLogo?: PopularBetsFallbackLogoModel;
            competitionLogos: { [competitionId: number]: PopularBetsFallbackLogoModel };
        };
    } = {};

    private logoConfigWithLowerCaseKeys: {
        [key: string]: string[];
    } = {};

    constructor(
        private sitecorePopularBetsConfig: SitecorePopularBetsConfig,
        private popularBetsConfig: PopularBetsConfig,
        private imagesConfig: ImagesConfig,
        private utilsService: UtilsService,
        private epcotConfig: EpcotConfig,
        private participantService: PopularBetsPodEventParticipantService,
    ) {
        this.processConfigs();
    }

    private processConfigs(): void {
        this.initializeLogoConfigWithLowerCaseKeys();
        this.preprocessCompetitionLogos();
    }

    getLogoDetailsForPodEvent(
        eventOption: EventOption,
        event: GridEvent,
        podType: PopularBetsPodTypes,
        podOptionTeamInfo?: PopularBetsOptionTeamInfo[],
    ): PopularBetsPodEventLogoDetails {
        if (!this.logoConfigWithLowerCaseKeys[podType.toLowerCase()]?.length) {
            this.processConfigs();
        }

        const logoConfigurationForPodType = this.logoConfigWithLowerCaseKeys[podType.toLowerCase()];

        // Early return based on config and epcot conditions
        if (!logoConfigurationForPodType?.length && this.epcotConfig.isEnabled) {
            return { logoType: 'OpenBullets' };
        }

        if (!this.epcotConfig.isEnabled && podType === PopularBetsPodTypes.SGP) {
            return { logoType: 'RadioOff' };
        }

        // Find the first valid logo details based on the logo configuration
        for (const logoType of logoConfigurationForPodType) {
            const logoDetails = this.getLogoDetailsByLogoType(logoType, eventOption, event, podOptionTeamInfo);
            if (logoDetails) return logoDetails;
        }

        // Return found logo details or default to 'RadioOff'
        return { logoType: 'RadioOff' };
    }

    private initializeLogoConfigWithLowerCaseKeys(): void {
        for (const [key, value] of Object.entries(this.popularBetsConfig.logoConfigurationForPodType)) {
            this.logoConfigWithLowerCaseKeys[key.toLowerCase()] = value;
        }
    }

    private preprocessCompetitionLogos(): void {
        for (const item of this.sitecorePopularBetsConfig.logos) {
            const { sportId, competitionId } = item;

            const dicRef = (this.logosDict[sportId] ??= { sportLogo: undefined, competitionLogos: {} });
            if (!competitionId) {
                dicRef.sportLogo = item;
            } else {
                dicRef.competitionLogos[competitionId] = item;
            }
        }
    }

    private getLogoDetailsByLogoType(
        logoType: string,
        eventOption: EventOption,
        event: GridEvent,
        podOptionTeamInfo?: PopularBetsOptionTeamInfo[],
    ): PopularBetsPodEventLogoDetails | null {
        switch (logoType) {
            case 'TeamLogo':
                return this.getTeamLogoDetails(eventOption, event, podOptionTeamInfo);
            case 'CompetitionLogo':
                return this.getCompetitionLogoDetails(event);
            case 'ShieldIcon':
                return this.getShieldIconDetails(event);
            case 'SportsLogo':
                return { logoType: 'SportsLogo' };
            default:
                return null;
        }
    }

    private getTeamLogoDetails(
        eventOption: EventOption,
        event: GridEvent,
        podOptionTeamInfo?: PopularBetsOptionTeamInfo[],
    ): PopularBetsPodEventLogoDetails | null {
        const participantLogo = this.getParticipantLogo(eventOption, event, podOptionTeamInfo);
        return participantLogo && { logoType: 'TeamLogo', participantLogo };
    }

    private getCompetitionLogoDetails(event: GridEvent): PopularBetsPodEventLogoDetails | null {
        const competitionLogoUrl = this.getCompetitionLogoUrl(event);
        return competitionLogoUrl ? { logoType: 'CompetitionLogo', fallbackLogoUrl: this.getCompetitionLogoUrl(event) } : null;
    }

    private getShieldIconDetails(event: GridEvent): PopularBetsPodEventLogoDetails {
        return {
            logoType: 'ShieldIcon',
            fallbackLogoUrl: this.utilsService.format(
                this.imagesConfig.participantImageUrl,
                ImageRootPath.Live,
                event.sport.id,
                FallbackImage.EpcotDefault,
            ),
        };
    }

    private getParticipantLogo(
        eventOption: EventOption,
        event: GridEvent,
        podOptionTeamInfo?: PopularBetsOptionTeamInfo[],
    ): EventParticipantImage | null {
        const optionTeamId = podOptionTeamInfo ? this.participantService.getOptionTeamId(eventOption, podOptionTeamInfo) : null;
        const participantId = optionTeamId ?? (event.offerSource === OfferSource.V1 ? eventOption.playerId : eventOption.optionPlayerId);

        if (!participantId) return null;

        const participant = event.participants.find((p: EventParticipant) =>
            // TeamId always maps to p.id. If TeamId exists or the source is V1, check p.id;
            // otherwise, check p.fixtureParticipantId for V2.
            optionTeamId || event.offerSource === OfferSource.V1 ? p.id === participantId : p.fixtureParticipantId === participantId,
        );

        return participant?.image?.logo || participant?.image?.jersey ? participant.image : null;
    }

    private getCompetitionLogoUrl(event: GridEvent): string | undefined {
        const { sport, league } = event;
        const competitionId = league.parentLeagueId ?? league.id;

        return this.logosDict[sport.id]?.competitionLogos[competitionId]?.src ?? this.logosDict[sport.id]?.sportLogo?.src;
    }
}
